import { rem } from 'polished'
import React, { useCallback, useContext, useRef } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import type { BasicTrip } from 'tripPlanner/types/common'
import type { TripItem } from 'tripPlanner/types/tripItem'
import TripPlannerFloatingToggleButton from './TripPlannerFloatingToggleButton'
import DropdownList from 'LuxKit/Dropdown/List/DropdownList'
import noop from 'lib/function/noop'
import ModalContext from 'contexts/ModalContext'
import ModalBase from 'LuxKit/Modal/ModalBase'
import ModalHeader from 'LuxKit/Modal/ModalHeader'
import ModalBody from 'LuxKit/Modal/ModalBody'
import ModalContent from 'LuxKit/Modal/ModalContent'
import TripList from './TripList'
import { useScreenSizeOnly } from 'hooks/useScreenSize'

const TripPlannerToggleContainer = styled.div`
  position: absolute;
  bottom: ${rem(28)};
  left: ${rem(12)};
`

export interface SelectedTrip {
  name: string
  tripId?: string
  items: Array<TripItem>
}

interface Props {
  trips: Array<BasicTrip>
  selectedTrip: SelectedTrip
  handleSelectTrip: (tripName: string, tripId?: string) => void
  dropDownVisible?: boolean
  toggleDropdownVisibility?: (e: React.MouseEvent<HTMLButtonElement> | undefined, overRideVal?: boolean) => void
  tripPlannerToggled?: boolean
  setTripPlannerToggled?: (val: boolean) => void
  hideAllTrips?: () => void
  className?: string
}

function TripPlannerToggle({
  trips,
  selectedTrip,
  handleSelectTrip,
  dropDownVisible = false,
  toggleDropdownVisibility = noop,
  className,
}: Props) {
  const tripPlannerButtonRef = useRef<HTMLButtonElement>(null)
  const intl = useIntl()

  const tripsTitle = intl.formatMessage({
    id: 'tXjk92zQgD',
    defaultMessage: 'Trips',
    description: 'Modal title for the trip planner trips list',
  })

  const onClose = useCallback(() => {
    toggleDropdownVisibility(undefined, false)
  }, [toggleDropdownVisibility])

  const showModal = useContext(ModalContext)

  const handleShowMobileModal = useCallback(() => {
    showModal(
      <ModalBase>
        <ModalHeader title={tripsTitle} />
        <ModalBody>
          <ModalContent>
            <TripList
              trips={trips}
              handleSelectTrip={handleSelectTrip}
              selectedTrip={selectedTrip}
            />
          </ModalContent>
        </ModalBody>
      </ModalBase>,
    )
  }, [handleSelectTrip, selectedTrip, showModal, trips, tripsTitle])

  const isMobile = useScreenSizeOnly('mobile')

  const onFloatingButtonClick = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault()
    isMobile ? handleShowMobileModal() : toggleDropdownVisibility(e)
  }, [handleShowMobileModal, isMobile, toggleDropdownVisibility])

  return (
    <>
      <TripPlannerToggleContainer className={className}>
        <TripPlannerFloatingToggleButton
          onClick={onFloatingButtonClick}
          isOpen={dropDownVisible}
          ref={tripPlannerButtonRef}
        />
      </TripPlannerToggleContainer>
      <DropdownList
        size="S"
        open={dropDownVisible}
        placement="top-start"
        triggerRef={tripPlannerButtonRef}
        onClose={onClose}
        anchorRef={tripPlannerButtonRef}
      >
        <TripList
          trips={trips}
          handleSelectTrip={handleSelectTrip}
          selectedTrip={selectedTrip}
        />
      </DropdownList>
    </>

  )
}

export default TripPlannerToggle
